import { Alert, Button, EmptyState } from '@skupreme/shared/ui';
import { toReviewTotal, useMappingCounts } from '@features/catalog-mapping';
import type { useCatalogList } from '../../hooks/use-catalog-list';
import { useCatalogMarkets } from '../../hooks/use-catalog-markets';
import { useMarketSelection } from '../../hooks/use-market-selection';
import { facetParams, marketName } from '../../lib/markets-api';
import { CatalogSectionTabs } from '../header/CatalogSectionTabs';
import { CatalogV2Filters } from './CatalogV2Filters';
import { ARCHIVED_VIEW } from '../../lib/v2';
import { CatalogV2Table } from './CatalogV2Table';
import { CatalogMarketFilter } from './CatalogMarketFilter';

export type CatalogV2ListProps = {
  list: ReturnType<typeof useCatalogList>;
};

/** EN-4134: the API sorts by available units up to a cap; above it the rows come by title, and the page says so. */
const sortFellBack = (notes: string[] | undefined) =>
  !!notes?.some(note => note.startsWith('sort_by=available'));

/** The V2 list screen body (filters and table) of the catalog page; the components page stays legacy. */
export function CatalogV2List({ list }: CatalogV2ListProps) {
  /* EN-3966: the Mapping pill counts what waits on a person: suggestions to confirm and listings with no match. */
  const { data: mappingCounts } = useMappingCounts();
  const markets = useCatalogMarkets(
    facetParams(list.filter, list.v2Filter, list.appliedSearch),
  );
  const market = useMarketSelection();
  /* M2: nothing listed in the countries picked; the way back is one press. */
  const noneListed =
    market.countries.length > 0 &&
    market.onlyListed &&
    !list.loading &&
    list.v2Data.length === 0;

  return (
    <>
      <CatalogSectionTabs
        counts={{
          products: list.totalData,
          mapping: toReviewTotal(mappingCounts),
        }}
      >
        <CatalogV2Filters
          rows={list.v2Data}
          search={list.search}
          onSearch={list.setSearch}
          filter={list.v2Filter}
          onFilterChange={list.v2FilterChanged}
          sort={list.filter.sort}
          onSortChange={sort => list.filterChanged('sort', sort)}
          onDivisionChange={() => list.pageChanged(1)}
          divisionCountries={markets.divisionCountries}
        />
        {markets.available ? (
          <CatalogMarketFilter markets={markets.countries} />
        ) : null}
      </CatalogSectionTabs>
      <div className="sk:flex sk:flex-col sk:gap-3 sk:px-3 sk:pb-6">
        {sortFellBack(list.v2NotSupported) ? (
          <Alert tone="info">
            This catalog is too large to sort by available units, so it is
            sorted by title. Narrow it with a filter or a search to sort by
            units.
          </Alert>
        ) : null}
        {noneListed ? (
          <EmptyState
            title={`No items listed in ${market.countries.map(marketName).join(', ')}${
              markets.divisionsFiltered ? ' for these divisions' : ''
            }`}
            action={
              <Button variant="pill" size="md" onPress={market.clear}>
                Show all marketplaces
              </Button>
            }
          />
        ) : (
          <CatalogV2Table
            rows={list.v2Data}
            archivedView={list.v2Filter.state === ARCHIVED_VIEW}
            loading={list.loading}
            page={list.filter.page}
            pageSize={list.filter.limit}
            totalData={list.totalData}
            onPageChange={list.pageChanged}
            onPageSizeChange={size => list.filterChanged('limit', size)}
            sort={list.filter.sort}
            onSortChange={sort => list.filterChanged('sort', sort)}
            noDataMessage="No Catalog Data Found"
          />
        )}
      </div>
    </>
  );
}
